import * as React from 'react';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { formatBerlinDateTime } from '@/lib/berlin-time';

interface DocumentFile {
  id: string;
  name: string;
  contentType: string;
  size?: number;
  url: string | null;
  status?: 'approved' | 'rejected' | 'submitted';
  uploadedAt?: number;
  patientCount?: number;
}

interface DocumentPreviewModalProps {
  documents: DocumentFile[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onDownload?: (doc: DocumentFile) => void;
  onApprove?: (doc: DocumentFile) => void;
  onReject?: (doc: DocumentFile) => void;
  formatFileSize: (bytes: number | undefined) => string;
  allowOpenInNewTab?: boolean;
}

export const DocumentPreviewModal = ({ documents, currentIndex, onClose, onNavigate, onDownload, onApprove, onReject, formatFileSize, allowOpenInNewTab = true }: DocumentPreviewModalProps): React.JSX.Element => {
  const currentDoc = documents[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < documents.length - 1;

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target;
      if (e.defaultPrevented || (target instanceof HTMLElement && target.closest('button, a, input, textarea, select, [contenteditable="true"]'))) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrev) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && hasNext) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, hasPrev, hasNext, onNavigate]);

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className='bg-[oklch(0.9_0.1_145)] text-[oklch(0.3_0.15_145)] border-[oklch(0.7_0.12_145)]' variant='outline'>
            Bestätigt
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className='bg-[oklch(0.9_0.12_25)] text-[oklch(0.3_0.18_25)] border-[oklch(0.7_0.15_25)]' variant='outline'>
            Abgelehnt
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className='bg-[oklch(0.95_0.1_85)] text-[oklch(0.4_0.15_85)] border-[oklch(0.8_0.12_85)]' variant='outline'>
            Eingereicht
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '';
    return formatBerlinDateTime(timestamp);
  };

  const isImage = currentDoc?.contentType?.startsWith('image/');
  const isPdf = currentDoc?.contentType === 'application/pdf';

  return (
    <Dialog open={true} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent showCloseButton={false} className='h-dvh max-h-dvh w-screen max-w-none overflow-hidden rounded-none border-0 p-0 flex flex-col sm:h-[90dvh] sm:max-h-[90dvh] sm:w-full sm:max-w-5xl sm:rounded-lg sm:border'>
        <DialogClose
          aria-label='Vorschau schließen'
          className='absolute right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-20 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        >
          <X className='size-5' aria-hidden='true' />
        </DialogClose>

        {/* Header */}
        <DialogHeader className='border-b pb-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[calc(4rem+env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))]'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogTitle className='text-base font-semibold truncate'>{currentDoc?.name || 'Dokument'}</DialogTitle>
              {(currentDoc?.size || currentDoc?.uploadedAt || currentDoc?.patientCount !== undefined) && (
                <div className='flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground'>
                  {currentDoc?.size && <span>{formatFileSize(currentDoc.size)}</span>}
                  {currentDoc?.uploadedAt && (
                    <>
                      <span className='opacity-50'>•</span>
                      <span>{formatDate(currentDoc.uploadedAt)}</span>
                    </>
                  )}
                  {currentDoc?.patientCount !== undefined && (
                    <>
                      <span className='opacity-50'>•</span>
                      <span>{currentDoc.patientCount} Patienten</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              {getStatusBadge(currentDoc?.status)}
              <span className='text-xs text-muted-foreground tabular-nums'>
                {currentIndex + 1}/{documents.length}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Preview Area */}
        <div className='relative min-h-0 flex-1 overflow-hidden bg-muted/10'>
          {/* Navigation Buttons */}
          {hasPrev && (
            <Button variant='ghost' size='icon' className='absolute left-4 top-1/2 z-10 h-11 w-11 -translate-y-1/2 bg-background/80 backdrop-blur-sm hover:bg-background' onClick={() => onNavigate(currentIndex - 1)} aria-label='Vorheriges Dokument'>
              <ChevronLeft className='size-6' />
            </Button>
          )}
          {hasNext && (
            <Button variant='ghost' size='icon' className='absolute right-4 top-1/2 z-10 h-11 w-11 -translate-y-1/2 bg-background/80 backdrop-blur-sm hover:bg-background' onClick={() => onNavigate(currentIndex + 1)} aria-label='Nächstes Dokument'>
              <ChevronRight className='size-6' />
            </Button>
          )}

          {/* Document Content */}
          <div className='flex h-full items-center justify-center p-3 sm:p-6'>
            {currentDoc?.url ? (
              <>
                {isImage && <img src={currentDoc.url} alt={currentDoc.name} className='max-h-full max-w-full rounded-lg object-contain shadow-lg' />}
                {isPdf && (
                  <div className='flex h-full min-h-0 w-full flex-col gap-3'>
                    <embed src={currentDoc.url} type='application/pdf' className='min-h-0 w-full flex-1 rounded-lg shadow-lg' />
                    <div className='flex shrink-0 flex-wrap items-center justify-center gap-x-1 rounded-lg border bg-background px-3 py-1 text-center text-sm text-muted-foreground'>
                      {allowOpenInNewTab ? (
                        <>
                          <span>PDF wird nicht angezeigt? </span>
                          <Button variant='link' className='h-auto min-h-11 whitespace-normal px-1 py-2 text-sm' onClick={() => window.open(currentDoc.url ?? '', '_blank')}>
                            In neuem Tab öffnen
                          </Button>
                        </>
                      ) : (
                        <>
                          <span>Vorschau nicht sichtbar? </span>
                          <Button variant='link' className='h-auto min-h-11 whitespace-normal px-1 py-2 text-sm' onClick={() => onDownload?.(currentDoc)} disabled={!onDownload}>
                            Vertrag herunterladen
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {!isImage && !isPdf && (
                  <div className='text-center space-y-4'>
                    <div className='text-muted-foreground'>Keine Vorschau verfügbar</div>
                    <Button variant='outline' onClick={() => onDownload?.(currentDoc)}>
                      <Download className='size-4 mr-2' />
                      Herunterladen
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className='text-center text-muted-foreground'>Dokument nicht verfügbar</div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className='flex flex-wrap items-center justify-between gap-3 border-t bg-background pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-3'>
          <Button variant='outline' size='sm' className='h-11' onClick={() => currentDoc && onDownload?.(currentDoc)} disabled={!currentDoc?.url || !onDownload}>
            <Download className='size-4 mr-2' />
            Herunterladen
          </Button>

          {(onApprove || onReject) && currentDoc?.status === 'submitted' && (
            <div className='flex flex-wrap items-center gap-2'>
              {onReject && (
                <Button variant='outline' size='sm' className='h-11' onClick={() => onReject(currentDoc)}>
                  Ablehnen
                </Button>
              )}
              {onApprove && (
                <Button size='sm' className='h-11' onClick={() => onApprove(currentDoc)}>
                  Bestätigen
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
